import { APPLICATION_ROLES, type ApplicationRole } from '../auth/authentication-contract.ts';
import {
  isOpenWorkTabAreaMark,
  type OpenWorkTabAreaMark,
} from '../plugin-system/open-work-tab-area-mark.ts';

const CACHE_FAMILY_PREFIX = 'forumandlex:core:authenticated-shell-reload-preview:';
const CACHE_PREFIX = `${CACHE_FAMILY_PREFIX}v5:`;
const CACHE_RETENTION_MS = 5 * 60 * 1_000;
const MAX_CACHE_BYTES = 128_000;
const MAX_GROUPS = 20;
const MAX_ITEMS_PER_GROUP = 40;
const MAX_TABS = 12;

export interface AuthenticatedShellReloadPreviewSidebarGroup {
  collapsed: boolean;
  items: readonly { key: string; label: string }[];
  key: string;
  label: string;
}

export interface AuthenticatedShellReloadPreviewTab {
  active: boolean;
  areaMark: OpenWorkTabAreaMark | null;
  hasMenu: boolean;
  id: string;
  section: string | null;
  title: string;
}

export interface AuthenticatedShellReloadPreviewSnapshot {
  actionCount: number;
  avatarBadgeCount: number;
  cachedAt: number;
  firmName: string | null;
  profile: {
    avatarUrl: string | null;
    email: string | null;
    fullName: string | null;
    roles: readonly ApplicationRole[];
  };
  sidebarGroups: readonly AuthenticatedShellReloadPreviewSidebarGroup[];
  tabs: readonly AuthenticatedShellReloadPreviewTab[];
  userId: string;
}

type SnapshotInput = Omit<AuthenticatedShellReloadPreviewSnapshot, 'cachedAt' | 'userId'>;

function storageKey(userId: string): string {
  return `${CACHE_PREFIX}${userId}`;
}

function boundedText(value: unknown, maxLength: number): string | null {
  return typeof value === 'string' && value.length > 0 && value.length <= maxLength ? value : null;
}

function nullableText(value: unknown, maxLength: number): string | null | undefined {
  return value === null ? null : boundedText(value, maxLength) || undefined;
}

function isRole(value: unknown): value is ApplicationRole {
  return APPLICATION_ROLES.includes(value as ApplicationRole);
}

function parseSnapshot(value: unknown): AuthenticatedShellReloadPreviewSnapshot | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const candidate = value as Record<string, unknown>;
  const userId = boundedText(candidate.userId, 128);
  const cachedAt = candidate.cachedAt;
  const actionCount = candidate.actionCount;
  const avatarBadgeCount = candidate.avatarBadgeCount;
  const firmName = nullableText(candidate.firmName, 320);
  const profile = candidate.profile;
  const groups = candidate.sidebarGroups;
  const tabs = candidate.tabs;
  if (
    !userId ||
    typeof cachedAt !== 'number' ||
    !Number.isFinite(cachedAt) ||
    typeof actionCount !== 'number' ||
    !Number.isSafeInteger(actionCount) ||
    actionCount < 0 ||
    actionCount > 20 ||
    typeof avatarBadgeCount !== 'number' ||
    !Number.isSafeInteger(avatarBadgeCount) ||
    avatarBadgeCount < 0 ||
    avatarBadgeCount > 20 ||
    firmName === undefined ||
    !profile ||
    typeof profile !== 'object' ||
    Array.isArray(profile) ||
    !Array.isArray(groups) ||
    groups.length > MAX_GROUPS ||
    !Array.isArray(tabs) ||
    tabs.length > MAX_TABS
  ) {
    return null;
  }
  const profileRecord = profile as Record<string, unknown>;
  const avatarUrl = nullableText(profileRecord.avatarUrl, 2_048);
  const email = nullableText(profileRecord.email, 320);
  const fullName = nullableText(profileRecord.fullName, 320);
  if (avatarUrl === undefined || email === undefined || fullName === undefined) return null;
  if (!Array.isArray(profileRecord.roles) || !profileRecord.roles.every(isRole)) return null;

  const sidebarGroups = groups.flatMap((group) => {
    if (!group || typeof group !== 'object' || Array.isArray(group)) return [];
    const record = group as Record<string, unknown>;
    const key = boundedText(record.key, 320);
    const label = boundedText(record.label, 320);
    if (
      !key ||
      !label ||
      typeof record.collapsed !== 'boolean' ||
      !Array.isArray(record.items) ||
      record.items.length > MAX_ITEMS_PER_GROUP
    ) {
      return [];
    }
    const items = record.items.flatMap((item) => {
      if (!item || typeof item !== 'object' || Array.isArray(item)) return [];
      const itemRecord = item as Record<string, unknown>;
      const itemKey = boundedText(itemRecord.key, 1_024);
      const itemLabel = boundedText(itemRecord.label, 320);
      return itemKey && itemLabel ? [{ key: itemKey, label: itemLabel }] : [];
    });
    return items.length === record.items.length
      ? [{ collapsed: record.collapsed, items, key, label }]
      : [];
  });
  if (sidebarGroups.length !== groups.length) return null;

  const parsedTabs = tabs.flatMap((tab) => {
    if (!tab || typeof tab !== 'object' || Array.isArray(tab)) return [];
    const record = tab as Record<string, unknown>;
    const id = boundedText(record.id, 1_024);
    const title = boundedText(record.title, 320);
    const section = nullableText(record.section, 160);
    const areaMark = record.areaMark;
    if (
      !id ||
      !title ||
      section === undefined ||
      (areaMark !== null && !isOpenWorkTabAreaMark(areaMark)) ||
      typeof record.active !== 'boolean' ||
      typeof record.hasMenu !== 'boolean'
    ) {
      return [];
    }
    return [
      {
        active: record.active,
        areaMark,
        hasMenu: record.hasMenu,
        id,
        section,
        title,
      },
    ];
  });
  if (parsedTabs.length !== tabs.length || !parsedTabs.some((tab) => tab.active)) return null;

  return {
    actionCount,
    avatarBadgeCount,
    cachedAt,
    firmName,
    profile: {
      avatarUrl,
      email,
      fullName,
      roles: profileRecord.roles,
    },
    sidebarGroups,
    tabs: parsedTabs,
    userId,
  };
}

export function writeAuthenticatedShellReloadPreviewSessionCache({
  snapshot,
  userId,
}: {
  snapshot: SnapshotInput;
  userId: string;
}): void {
  if (typeof window === 'undefined' || !boundedText(userId, 128)) return;
  const parsed = parseSnapshot({ ...snapshot, cachedAt: Date.now(), userId });
  if (!parsed) return;
  try {
    const serialized = JSON.stringify(parsed);
    if (serialized.length > MAX_CACHE_BYTES) return;
    for (let index = window.sessionStorage.length - 1; index >= 0; index -= 1) {
      const key = window.sessionStorage.key(index);
      if (key?.startsWith(CACHE_FAMILY_PREFIX) && key !== storageKey(userId)) {
        window.sessionStorage.removeItem(key);
      }
    }
    window.sessionStorage.setItem(storageKey(userId), serialized);
  } catch {
    // Reload continuity is optional when browser storage is unavailable.
  }
}

export function readAuthenticatedShellReloadPreviewSessionCache({
  now = Date.now(),
  userId,
}: {
  now?: number;
  userId: string;
}): AuthenticatedShellReloadPreviewSnapshot | null {
  if (typeof window === 'undefined' || !boundedText(userId, 128)) return null;
  const key = storageKey(userId);
  try {
    const serialized = window.sessionStorage.getItem(key);
    if (!serialized || serialized.length > MAX_CACHE_BYTES) return null;
    const snapshot = parseSnapshot(JSON.parse(serialized));
    if (
      !snapshot ||
      snapshot.userId !== userId ||
      now < snapshot.cachedAt ||
      now - snapshot.cachedAt > CACHE_RETENTION_MS
    ) {
      window.sessionStorage.removeItem(key);
      return null;
    }
    return snapshot;
  } catch {
    return null;
  }
}

export function clearAuthenticatedShellReloadPreviewSessionCache(): void {
  if (typeof window === 'undefined') return;
  try {
    for (let index = window.sessionStorage.length - 1; index >= 0; index -= 1) {
      const key = window.sessionStorage.key(index);
      if (key?.startsWith(CACHE_FAMILY_PREFIX)) window.sessionStorage.removeItem(key);
    }
  } catch {
    // Authentication cleanup remains safe when browser storage is unavailable.
  }
}
